/** Single source of truth for app section navigation — consumed by the mobile
 *  bottom bar (MobileNav) and by AppShell's active-tab resolution. Keeping one
 *  list here is what prevents the drift where MobileNav knew only 3 of 5 routes. */
import type { ComponentType } from 'react';
import { BookmarkIcon, FolderIcon, TuneIcon, RuleIcon, SettingsIcon, CreditCardIcon } from './icons';

export type NavTabId = 'bookmarks' | 'categories' | 'processing' | 'review' | 'settings' | 'billing';

/** A `#`-prefixed route is a bar action, not a destination — the host
 *  intercepts it (e.g. Categories opens the sidebar drawer) instead of
 *  navigating, and `pathToNavTab` never matches it. */
export interface NavItem {
  id: NavTabId;
  label: string;
  Icon: ComponentType<{ size?: number }>;
  route: string;
  /** When set, the item only shows if this count > 0 OR its route is active.
   *  Lets Review stay discoverable without cluttering the bar, while never
   *  vanishing out from under a user who deep-linked to /app/review. */
  conditionalCount?: 'needs_review';
  /** Kept out of the mobile bar — Billing lives behind Settings / the plan
   *  badge on phones, where its slot is given to the more-used Categories. */
  hideInBar?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { id: 'bookmarks', label: 'Bookmarks', Icon: BookmarkIcon, route: '/app' },
  { id: 'categories', label: 'Categories', Icon: FolderIcon, route: '#categories' },
  { id: 'processing', label: 'Processing', Icon: TuneIcon, route: '/app/processing' },
  { id: 'review', label: 'Review', Icon: RuleIcon, route: '/app/review', conditionalCount: 'needs_review' },
  { id: 'settings', label: 'Settings', Icon: SettingsIcon, route: '/app/settings' },
  { id: 'billing', label: 'Billing', Icon: CreditCardIcon, route: '/app/billing', hideInBar: true },
];

/** Resolve the active tab from a pathname. '/app' is a prefix of every route, so
 *  match the more specific routes first and fall back to bookmarks. */
export function pathToNavTab(pathname: string): NavTabId {
  const match = NAV_ITEMS.find((i) => i.id !== 'bookmarks' && pathname.startsWith(i.route));
  return match?.id ?? 'bookmarks';
}

/** Whether a nav item should render, given live counts and the current route. */
export function isNavItemVisible(
  item: NavItem,
  counts: { needs_review?: number } | undefined,
  pathname: string,
): boolean {
  if (!item.conditionalCount) return true;
  const count = counts?.[item.conditionalCount] ?? 0;
  return count > 0 || pathToNavTab(pathname) === item.id;
}
